import { PageTopBar, ThreeSlotLayout } from "../../../../../common/components/page_top_bar";
import { useChecklistServicoListaViewModel } from "../../viewModels/checklist_servico_lista_view_model";
import { Breadcrumbs, Typography, Link, Stack, TablePagination } from "@mui/material";
import { Header } from "./components/header";
import { StyledDataGrid } from "../../../../../common/components/styled_data_grid";
import { useMemo } from "react";
import { GenerateChecklistServicoColumnsDefinition } from "./columns";
import { ChecklistServico } from "../../../domain/models/checklist_servico";
import { ChecklistServicoModal } from "./components/checklist_servico_modal";
import { CopyChecklistServicoModal } from "./components/copy_checklist_servico_modal";
import { ReportViewerModal } from "../../../../../common/components/report_viewer_modal";
import { GridPinnedColumnFields } from "@mui/x-data-grid";

import DynamicIcon from "../../../../../common/components/dynamic_icon";
import PrimaryButton from "../../../../../common/components/primary_button";
import AlertError from "../../../../../common/components/alert_error";
import RefreshButton from "../../../../../common/components/refresh_button";
import DeleteDialog from "../../../../../common/components/delete_dialog";

/**
 * ChecklistServicoLista
 */

export const ChecklistServicoLista = () => {
  const viewModel = useChecklistServicoListaViewModel();

  const columnsDefinition = useMemo(() => {
    return GenerateChecklistServicoColumnsDefinition({
      onActionEditar: viewModel.handleChecklistServicoModal,
      onActionDeletar: viewModel.handleChecklistServicoDeleteModal,
      onActionCopiar: viewModel.handleCopyChecklistServicoModal,
      onEnterChecklists: viewModel.handleEnterChecklist,
      onEnterChecklistsLiberacao: viewModel.handleEnterChecklistLiberacao,
      onEnterVisualize: viewModel.handleEnterVisualize,
      onActionExportar: viewModel.handleExportar,
    });
  }, [
    viewModel.handleChecklistServicoModal,
    viewModel.handleChecklistServicoDeleteModal,
    viewModel.handleCopyChecklistServicoModal,
    viewModel.handleEnterChecklistLiberacao,
    viewModel.handleEnterChecklist,
    viewModel.handleEnterVisualize,
    viewModel.handleExportar,
  ]);

  const pinnedColumns = useMemo((): GridPinnedColumnFields | undefined => {
    return viewModel.checklistServicos && viewModel.checklistServicos.length > 0
      ? { right: ['idChecklistServico'] }
      : undefined;
  }, [
    viewModel.checklistServicos,
  ]);

  return (
    <>
      {/**
        * PageTopBar
        */}

      <PageTopBar>
        <ThreeSlotLayout
          rightSlot={
            <div className="flex flex-row gap-2">
              <div className="w-[115px] ">
                <PrimaryButton
                  buttonColor="yellow"
                  onMouseDown={e => viewModel.handleRelatorioLista()}>
                  Relatório
                </PrimaryButton>
              </div>

              <div className="w-[115px] ">
                <PrimaryButton
                  startIcon={<DynamicIcon nameIcon="AddRounded" size={25} className="text-white" />}
                  onMouseDown={e => viewModel.handleChecklistServicoModal(null, true)}>
                  Novo Item
                </PrimaryButton>
              </div>
            </div>
          }
        />
      </PageTopBar>

      {/**
        * Page
        */}

      <div className="flex w-full gap-2 flex-col py-2 px-4 mb-20">
        <Breadcrumbs aria-label="breadcrumb" className="cursor-pointer">
          <Link underline="hover" color="inherit">
            Obras
          </Link>
          <Typography className="text-primary-green">
            Checklist de Serviço
          </Typography>
        </Breadcrumbs>

        <div className="div flex justify-between items-center flex-col md:flex-row gap-6 md:gap-0">
          <div className="flex gap-2 items-center w-full">
            <h1 className="text-3xl font-medium text-black dark:text-white">Checklist de Serviço</h1>
            <RefreshButton onClick={viewModel.handleReload} />
          </div>

          <div className="w-full justify-end flex gap-2"></div>
        </div>
        {
          viewModel.errorMessage !== undefined
            ? (
              <div className="w-full">
                <AlertError
                  messageErro={viewModel.errorMessage}
                  onTryAgain={viewModel.handleReload} />
              </div>
            ) : (
              <>
                <Header
                  tipoCampo={viewModel.filters.tipoCampo}
                  valorCampo={viewModel.filters.valorCampo}
                  status={viewModel.filters.status}
                  onTipoCampoChange={viewModel.handleTipoCampoChange}
                  onValorCampoChange={viewModel.handleValorCampoChange}
                  onStatusChange={viewModel.handleStatusChange}
                  onClearFilters={viewModel.handleClearFilters}
                />

                <Stack spacing={0}>
                  <TablePagination
                    component="div"
                    count={viewModel.pagination.totalItems}
                    page={viewModel.pagination.page - 1}
                    onPageChange={(_, p) => viewModel.handlePageChange(p)}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    rowsPerPage={viewModel.pagination.rowsPerPage}
                    onRowsPerPageChange={(e) => viewModel.handleRowsPerPageChange(parseInt(e.target.value))}
                  />

                  <StyledDataGrid
                    tableName="checklist_servico_lista"
                    disableColumnMenu={false}
                    getRowId={(row: ChecklistServico) => row.idChecklistServico}
                    getRowHeight={() => 'auto'}
                    textSize="pequeno"
                    columns={columnsDefinition}
                    loading={viewModel.loading}
                    rows={viewModel.checklistServicos}
                    rowCount={viewModel.checklistServicos.length}
                    pinnedColumns={pinnedColumns}
                    pageSizeOptions={[10, 25, 50, 100]}
                    paginationMode="server"
                    paginationModel={{
                      page: viewModel.pagination.page,
                      pageSize: viewModel.pagination.rowsPerPage,
                    }}
                    sx={{
                      '& .ia-header': {
                        backgroundColor: "#26C281",
                      }
                    }}
                  />

                  <TablePagination
                    component="div"
                    count={viewModel.pagination.totalItems}
                    page={viewModel.pagination.page - 1}
                    onPageChange={(_, p) => viewModel.handlePageChange(p)}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    rowsPerPage={viewModel.pagination.rowsPerPage}
                    onRowsPerPageChange={(e) => viewModel.handleRowsPerPageChange(parseInt(e.target.value))}
                  />

                </Stack>
              </>
            )
        }
      </div>

      {/**
        * Modals
        */}

      <ChecklistServicoModal
        open={viewModel.checklistServicoModal}
        saving={viewModel.saving}
        loading={viewModel.loading}
        selectedChecklistServico={viewModel.selectedChecklistServico ?? null}
        onClose={viewModel.handleChecklistServicoModal}
        onSubmit={viewModel.handleChecklistServicoSubmit}
      />

      <CopyChecklistServicoModal
        open={viewModel.copyChecklistServicoModal}
        saving={viewModel.saving}
        loading={viewModel.loading}
        selectedChecklistServico={viewModel.selectedChecklistServico ?? null}
        onClose={viewModel.handleCopyChecklistServicoModal}
        onSubmit={viewModel.handleCopyChecklistServicoSubmit}
      />

      <DeleteDialog
        open={viewModel.checklistServicoDeleteModal}
        saving={viewModel.saving}
        title='Você realmente deseja excluir este item?'
        onClose={() => viewModel.handleChecklistServicoDeleteModal(null, false)}
        onDelete={viewModel.handleChecklistServicoDeleteSubmit}
      />

      <ReportViewerModal
        reportViewerState={viewModel.reportViewerState}
      />

    </>
  );
}